// Razorpay web checkout service.
// Mirrors the `web` code path from r2b-restaurant-mobile-new/src/services/razorpay.js.

const RAZORPAY_KEY_ID = 'rzp_live_pEFQuRJCHOQJ0E';
const RAZORPAY_SCRIPT_URL = 'https://checkout.razorpay.com/v1/checkout.js';

const loadRazorpayScript = () =>
  new Promise((resolve) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      resolve(false);
      return;
    }
    const existing = document.querySelector(`script[src="${RAZORPAY_SCRIPT_URL}"]`);
    if (existing) {
      // If it's already in the DOM and loaded, resolve true; otherwise attach load listener.
      if (window.Razorpay) {
        resolve(true);
        return;
      }
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', () => resolve(false));
      return;
    }
    const script = document.createElement('script');
    script.src = RAZORPAY_SCRIPT_URL;
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

/**
 * Opens the Razorpay checkout modal with the given options.
 * Resolves with { success: true, paymentId, orderId, signature }
 * or { success: false, error } on dismissal / failure.
 */
const openCheckout = async (options) => {
  const loaded = await loadRazorpayScript();
  if (!loaded || typeof window === 'undefined' || !window.Razorpay) {
    return { success: false, error: 'Failed to load Razorpay' };
  }
  return new Promise((resolve) => {
    const rzp = new window.Razorpay({
      ...options,
      handler: (response) => {
        resolve({
          success: true,
          paymentId: response.razorpay_payment_id,
          orderId: response.razorpay_order_id,
          signature: response.razorpay_signature,
        });
      },
      modal: {
        ondismiss: () => resolve({ success: false, error: 'Payment cancelled by user' }),
      },
    });
    rzp.open();
  });
};

const handlePaymentSuccess = (paymentId, orderId, signature) => ({
  razorpayPaymentId: paymentId,
  razorpayOrderId: orderId,
  razorpaySignature: signature,
});

const showPaymentDialog = () => Promise.resolve(true);

const formatAmount = (amount) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(amount);

const validateAmount = (amount) => amount > 0 && amount <= 500000;

const baseOptions = (orderId, amount, description, userDetails) => ({
  key: RAZORPAY_KEY_ID,
  amount: Math.round(amount * 100),
  currency: 'INR',
  name: 'Roll2Bowl Technologies',
  description,
  order_id: orderId,
  prefill: userDetails,
  theme: { color: '#851E41' },
});

const createSubscriptionPurchaseOptions = (orderId, amount, planName, userDetails) =>
  baseOptions(orderId, amount, `Subscription purchase - ${planName}`, userDetails);

const createSubscriptionUpgradeOptions = (orderId, amount, planName, userDetails) =>
  baseOptions(orderId, amount, `Subscription upgrade - ${planName}`, userDetails);

const createSubscriptionRenewalOptions = (orderId, amount, planName, userDetails) =>
  baseOptions(orderId, amount, `Subscription renewal - ${planName}`, userDetails);

const createTopUpOptions = (orderId, amount, orderCount, userDetails) =>
  baseOptions(orderId, amount, `Order count top-up - ${orderCount} orders`, userDetails);

const razorpayService = {
  openCheckout,
  handlePaymentSuccess,
  showPaymentDialog,
  formatAmount,
  validateAmount,
  createSubscriptionPurchaseOptions,
  createSubscriptionUpgradeOptions,
  createSubscriptionRenewalOptions,
  createTopUpOptions,
};

export default razorpayService;
